import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { APP_VERSION_LABEL } from '@/version';
import { shouldShowAppleSignInUi } from '@/utils/authPolicy';
import {
  ExternalLink,
  ChevronRight, Check, AlertTriangle,
  Loader2, Mail, Sparkles,
} from 'lucide-react';
import { PageWrapper } from '../components/PageWrapper';
import { WebSubscriptionGate } from '../components/WebSubscriptionGate';
import { NativeSubscriptionOptions, SubscriptionLegalCopy, TERMS_OF_USE_URL } from '../components/NativeSubscriptionOptions';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  getSettings, saveSettings,
  isPasscodeEnabled, setPasscodeEnabled, savePasscode, clearPasscode,
  getNotificationSettings, saveNotificationSettings,
  clearAIWelcomeCache,
  saveTextScale,
  getTextScale,
  saveColorTheme,
  resetLocalAppAfterAccountDeletion,
  TEXT_SCALES,
  getVoicePrefs,
  saveVoicePrefs,
  getCustomThemePalette,
  saveCustomThemePalette,
  SECURITY_TYPE,
  getSecurityType,
  setSecurityType,
  formatDate,
  AI_DAILY_TOKEN_BUDGET_DISABLED,
} from '@/utils/storage';
import { applyThemeIcon } from '@/utils/themeIcon';
import {
  isNotificationSupported, requestNotificationPermission,
  scheduleNotification, cancelNotification,
} from '../utils/notifications';
import { useTheme } from '../components/ThemeProvider';
import { toast } from 'sonner';
import { isBiometricsAvailable, authenticateWithBiometrics } from '@/utils/biometricAuth';
import { loadSpeechVoices } from '@/utils/chatVoice';
import { buildCustomThemeFromColorInput } from '@/utils/customThemePalette';
import {
  signOutFirebase,
  subscribeToAuth,
  deleteFirebaseAccount,
  refreshRecentLoginForCurrentUser,
  linkCurrentUserWithGoogle,
  linkCurrentUserWithApple,
  getLinkedProviderIdsLive,
} from '@/utils/firebase';
import { deleteCloudAccountData, getCloudUserPreferences } from '@/utils/cloudApi';
import {
  requiresNativeSubscriptionGate,
  openNativeSubscriptionManagement,
  restoreNativePurchases,
  checkActiveProSubscription,
  purchaseNativeSubscription,
  getNativeStoreSubscriptionDetails,
} from '@/utils/nativeSubscriptions';
import {
  formatAiTokenMeterLine,
  setCachedNativeSubActiveForUid,
  getAiUsageTierForUid,
  shouldShowSettingsAiTokenMeterForUid,
  isNativeProSubCachedActive,
} from '@/utils/aiTokenEntitlements';
import {
  readCachedFirestorePro,
  refreshUserFirestoreEntitlement,
  isFirestoreProEntitlementActive,
} from '@/utils/userFirestoreEntitlement';
import { isPayPalWebShell } from '@/utils/payPalWebUi';

/** Set to true when native IAP / optional web checkout should show under Account. */
const SHOW_SETTINGS_SUBSCRIPTION_UI = true;

/** Theme grid: left column, then right column per row */
const THEME_PICKER_ROWS = [
  [{ kind: 'preset', key: 'pure' }, { kind: 'preset', key: 'slate' }],
  [{ kind: 'preset', key: 'sunrise' }, { kind: 'preset', key: 'sunset' }],
  [{ kind: 'preset', key: 'skyblue' }, { kind: 'preset', key: 'midnightblue' }],
  [{ kind: 'preset', key: 'sage' }, { kind: 'preset', key: 'forestgreen' }],
  [{ kind: 'preset', key: 'wisteria' }, { kind: 'preset', key: 'aubergine' }],
  [{ kind: 'custom', dark: false }, { kind: 'custom', dark: true }],
];

const themeSwatchClass = (active) =>
  `relative min-w-0 rounded-xl border-2 p-3 text-left transition-all overflow-hidden ${
    active ? 'border-theme-accent' : 'border-theme-secondary hover:border-theme-primary'
  }`;

const getContrastTextColor = (hex) => {
  if (!hex || typeof hex !== 'string') return '#2D2D35';
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return '#2D2D35';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#2D2D35' : '#F9F9FF';
};

const formatUiError = (error) => {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error?.message) return String(error.message);
  if (error?.code) return String(error.code);
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const GoogleLogo = ({ className = '' }) => (
  <svg viewBox="0 0 48 48" aria-hidden="true" className={className}>
    <path fill="#EA4335" d="M24 9.5c3.1 0 5.9 1.1 8.1 3.1l6-6C34.4 3.2 29.6 1 24 1 14.6 1 6.4 6.4 2.5 14.1l7 5.4C11.2 13.3 17.1 9.5 24 9.5z" />
    <path fill="#34A853" d="M24 47c6.5 0 12-2.1 16-5.8l-7.7-6.1c-2.2 1.5-5 2.4-8.3 2.4-6.8 0-12.6-3.8-14.6-9.3l-7 5.4C6.3 41.4 14.5 47 24 47z" />
    <path fill="#4A90E2" d="M40 41.2C44.6 37 47 30.8 47 24c0-1.6-.2-3.2-.4-4.7H24v9h12.9c-.6 2.9-2.2 5.4-4.6 6.8l7.7 6.1z" />
    <path fill="#FBBC05" d="M9.4 28.2c-.5-1.4-.8-2.8-.8-4.2s.3-2.9.8-4.2l-7-5.4C.9 17.4 0 20.6 0 24s.9 6.6 2.4 9.6l7-5.4z" />
  </svg>
);

const AppleLogo = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M16.7 12.8c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.2-2.8.8-3.6.8-.8 0-2-.8-3.2-.8-1.7 0-3.2 1-4 2.5-1.7 2.9-.4 7.2 1.2 9.6.8 1.2 1.7 2.5 3 2.4 1.2-.1 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.3.9-1.3 1.3-2.6 1.3-2.7-.1 0-2.5-1-2.5-4.2zM14.4 6c.6-.8 1-1.9.9-3-.9 0-2.1.7-2.8 1.4-.6.7-1.1 1.8-.9 2.9 1 .1 2.1-.6 2.8-1.3z" />
  </svg>
);

export const Settings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(getSettings() || { name: '', goals: [] });
  const refreshSettings = () => setSettings(getSettings());

  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');

  const { theme, changeTheme, themes } = useTheme();
  const [textScale, setTextScale] = useState(getTextScale());
  const [voicePrefs, setVoicePrefsState] = useState(() => getVoicePrefs());
  const [ttsVoices, setTtsVoices] = useState([]);
  const [customColorInput, setCustomColorInput] = useState(() => getCustomThemePalette()?.baseHex || '#B565D8');
  const [customVariantDark, setCustomVariantDark] = useState(() => getCustomThemePalette()?.variant === 'dark');
  const [customThemeDialogOpen, setCustomThemeDialogOpen] = useState(false);
  const [customDialogDark, setCustomDialogDark] = useState(false);

  // Passcode dialog state
  const [passcodeEnabled, setLocalPasscodeEnabled] = useState(isPasscodeEnabled());
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false);
  const [passcodeValue, setPasscodeValue] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [passcodeStep, setPasscodeStep] = useState('enter');

  // NEW SECURITY STATE
  const [securityType, setSecurityTypeState] = useState(() => {
    return getSecurityType() || 'none';
  });
  const [biometricsAvailable, setBiometricsAvailable] = useState(false);

  // Notifications
  const [notifSettings, setNotifSettings] = useState(getNotificationSettings());
  const [notifSupported, setNotifSupported] = useState(true);

  // AI
  const [currentUser, setCurrentUser] = useState(null);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isLinkingProvider, setIsLinkingProvider] = useState('');
  const [linkedProviderIds, setLinkedProviderIds] = useState([]);
  const canShowAppleAuth = shouldShowAppleSignInUi();
  const [storeSubMeta, setStoreSubMeta] = useState({ plan: null, expiresAt: null });
  const [accountEntitleTick, setAccountEntitleTick] = useState(0);
  const [aiUsageTick, setAiUsageTick] = useState(0);

  // EFFECTS
  useEffect(() => {
    setNotifSupported(isNotificationSupported());
  }, []);

  useEffect(() => {
    const checkBiometrics = async () => {
      const available = await isBiometricsAvailable();
      setBiometricsAvailable(available);
    };
    checkBiometrics();
  }, []);

  useEffect(() => subscribeToAuth(setCurrentUser), []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') setAiUsageTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    const h = () => {
      setAccountEntitleTick((t) => t + 1);
      setAiUsageTick((t) => t + 1);
    };
    window.addEventListener('rebuildme-entitlement-changed', h);
    return () => window.removeEventListener('rebuildme-entitlement-changed', h);
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) return;
    void refreshUserFirestoreEntitlement(currentUser.uid);
  }, [currentUser?.uid, accountEntitleTick]);

  useEffect(() => {
    let cancelled = false;
    void loadSpeechVoices().then((list) => {
      if (!cancelled) setTtsVoices(Array.isArray(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const p = getCustomThemePalette();
    if (p?.baseHex) setCustomColorInput(p.baseHex);
    setCustomVariantDark(p?.variant === 'dark');
  }, [theme]);

  const updateVoicePrefs = (partial) => {
    const next = saveVoicePrefs(partial);
    setVoicePrefsState(next);
  };

  // SECURITY HANDLERS
  const handlePasscodeToggle = async (enabled) => {
    if (enabled) {
      setShowPasscodeDialog(true);
      setPasscodeStep('enter');
      setPasscodeValue('');
      setConfirmPasscode('');
    } else {
      clearPasscode();
      setSecurityType('none');
      setSecurityTypeState('none');
      toast.success('Passcode disabled');
    }
  };

  const handleBiometricsToggle = async (enabled) => {
    if (enabled) {
      if (!biometricsAvailable) {
        toast.error('Face ID / Touch ID is not available on this device');
        return;
      }
      
      const result = await authenticateWithBiometrics('Enable biometrics to unlock RE-BUILD-ME-PRO');
      if (result.success) {
        clearPasscode();
        setSecurityType('biometric');
        setSecurityTypeState('biometric');
        toast.success('Face ID / Touch ID enabled');
      } else {
        toast.error('Could not enable biometrics');
      }
    } else {
      setSecurityType('none');
      setSecurityTypeState('none');
      toast.success('Face ID / Touch ID disabled');
    }
  };

  // Passcode dialog submit
  const handlePasscodeSubmit = () => {
    if (passcodeStep === 'enter') {
      if (passcodeValue.length === 4) { 
        setPasscodeStep('confirm'); 
        setConfirmPasscode(''); 
      }
    } else {
      if (confirmPasscode === passcodeValue) {
        savePasscode(passcodeValue);
        setPasscodeEnabled(true);
        setLocalPasscodeEnabled(true);
        setShowPasscodeDialog(false);
        toast.success('Passcode enabled');
      } else {
        toast.error('Passcodes do not match');
        setPasscodeStep('enter');
        setPasscodeValue('');
        setConfirmPasscode('');
      }
    }
  };

  const handleTextScaleChange = (scaleKey) => {
    saveTextScale(scaleKey);
    setTextScale(scaleKey);
    toast.success(`Text size set to ${scaleKey === 'xlarge' ? 'Extra Large' : scaleKey.charAt(0).toUpperCase() + scaleKey.slice(1)}`);
  };

  const handleDeleteCloudAccount = async () => {
    if (!currentUser || isDeletingAccount) return;

    let cloudDeleteSucceeded = false;
    try {
      setIsDeletingAccount(true);
      await refreshRecentLoginForCurrentUser();
      await deleteCloudAccountData();
      cloudDeleteSucceeded = true;
      try {
        await deleteFirebaseAccount();
      } catch (firebaseErr) {
        const m = formatUiError(firebaseErr).toLowerCase();
        // Cloud API may have removed the Auth user already; treat as success for cleanup.
        if (
          !m.includes('user-not-found')
          && !m.includes('no signed-in')
          && !m.includes('no user')
        ) {
          throw firebaseErr;
        }
      }
      toast.success('Account deleted');
      setShowDeleteAccountDialog(false);
    } catch (error) {
      const message = formatUiError(error);
      if (message.includes('requires-recent-login') || message.toLowerCase().includes('recent authentication')) {
        toast.error('Please sign in again and retry delete account.');
      } else {
        toast.error(message || 'Could not delete account');
      }
      console.error('Delete account failed:', error);
    } finally {
      if (cloudDeleteSucceeded) {
        await signOutFirebase().catch(() => {});
        await resetLocalAppAfterAccountDeletion();
        changeTheme('midnightblue');
        await applyThemeIcon('midnightblue').catch(() => {});
        try {
          const { flushSqliteStorageWrites } = await import('@/utils/sqliteStorage');
          await flushSqliteStorageWrites();
        } catch {
          /* ignore */
        }
        // Full document load so in-memory React state cannot keep stale account slices.
        window.location.replace(`/?_acct=${Date.now()}`);
      }
      setIsDeletingAccount(false);
    }
  };

  const importLinkedProviderPreferences = async (providerLabel) => {
    const shouldImport = window.confirm(
      `Account linked successfully. Import your existing ${providerLabel} settings on this device now?`,
    );
    if (!shouldImport) {
      toast.message('Kept current device settings.');
      return;
    }
    try {
      const cloudPrefs = await getCloudUserPreferences();
      if (!cloudPrefs || typeof cloudPrefs !== 'object') {
        toast.message('No cloud settings found to import.');
        return;
      }
      if (cloudPrefs.settings && typeof cloudPrefs.settings === 'object') {
        const local = getSettings() || {};
        const s = cloudPrefs.settings;
        const mergedName = String(s.name || '').trim() || String(local.name || '').trim();
        const next = {
          ...local,
          name: mergedName,
          goals: Array.isArray(s.goals) ? s.goals : (Array.isArray(local.goals) ? local.goals : []),
          migrated: Boolean(local.migrated || s.migrated),
        };
        delete next.prompts;
        saveSettings(next);
        refreshSettings();
      }
      if (typeof cloudPrefs.colorTheme === 'string') {
        saveColorTheme(cloudPrefs.colorTheme);
        changeTheme(cloudPrefs.colorTheme);
      }
      if (cloudPrefs.customThemePalette && typeof cloudPrefs.customThemePalette === 'object') {
        saveCustomThemePalette(cloudPrefs.customThemePalette);
      }
      if (typeof cloudPrefs.textScale === 'string') {
        saveTextScale(cloudPrefs.textScale);
        setTextScale(cloudPrefs.textScale);
      }
      if (cloudPrefs.voicePrefs && typeof cloudPrefs.voicePrefs === 'object') {
        const next = saveVoicePrefs(cloudPrefs.voicePrefs);
        setVoicePrefsState(next);
      }
      toast.success(`Imported ${providerLabel} settings.`);
    } catch (error) {
      toast.error('Could not import cloud settings.');
      console.warn('Cloud preference import failed:', error);
    }
  };

  const hasGoogleLinked = linkedProviderIds.includes('google.com');
  const hasAppleLinked = linkedProviderIds.includes('apple.com');

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      if (!currentUser) {
        if (!cancelled) setLinkedProviderIds([]);
        return;
      }
      const ids = await getLinkedProviderIdsLive();
      if (!cancelled) setLinkedProviderIds(ids);
    };
    void refresh();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isLinkingProvider]);

  const handleLinkGoogle = async () => {
    if (isLinkingProvider) return;
    const liveIds = await getLinkedProviderIdsLive();
    if (liveIds.includes('google.com')) {
      setLinkedProviderIds(liveIds);
      toast.message('Google is already linked.');
      return;
    }
    setIsLinkingProvider('google');
    try {
      await linkCurrentUserWithGoogle();
      setLinkedProviderIds(await getLinkedProviderIdsLive());
      toast.success('Google account linked');
      await importLinkedProviderPreferences('Google');
    } catch (error) {
      toast.error(String(error?.message || 'Could not link Google account'));
    } finally {
      setIsLinkingProvider('');
    }
  };

  const handleLinkApple = async () => {
    if (isLinkingProvider) return;
    const liveIds = await getLinkedProviderIdsLive();
    if (liveIds.includes('apple.com')) {
      setLinkedProviderIds(liveIds);
      toast.message('Apple is already linked.');
      return;
    }
    setIsLinkingProvider('apple');
    try {
      await linkCurrentUserWithApple();
      setLinkedProviderIds(await getLinkedProviderIdsLive());
      toast.success('Apple account linked');
      await importLinkedProviderPreferences('Apple');
    } catch (error) {
      toast.error(String(error?.message || 'Could not link Apple account'));
    } finally {
      setIsLinkingProvider('');
    }
  };

  // Name handler
  const handleNameSave = () => {
    if (tempName.trim()) {
      const s = { ...settings, name: tempName.trim() };
      saveSettings(s);
      refreshSettings();
      setEditingName(false);
      clearAIWelcomeCache();
      toast.success('Name updated');
    }
  };

  // Notifications handlers
  const handleNotificationToggle = async (enabled) => {
    if (enabled) {
      const granted = await requestNotificationPermission();
      if (granted) {
        saveNotificationSettings(true, notifSettings.time);
        setNotifSettings({ ...notifSettings, enabled: true });
        await scheduleNotification(notifSettings.time);
        toast.success('Daily reminder enabled');
      } else { toast.error('Notification permission denied'); }
    } else {
      saveNotificationSettings(false, notifSettings.time);
      setNotifSettings({ ...notifSettings, enabled: false });
      await cancelNotification();
      toast.success('Daily reminder disabled');
    }
  };
  
  const handleTimeChange = async (e) => {
    const time = e.target.value;
    setNotifSettings({ ...notifSettings, time });
    saveNotificationSettings(notifSettings.enabled, time);
    if (notifSettings.enabled) {
      await scheduleNotification(time);
      toast.success(`Reminder set for ${time}`);
    }
  };

  const subscribeCheckoutUrl = (process.env.REACT_APP_SUBSCRIBE_CHECKOUT_URL || '').trim();
  const nativeIapConfigured = requiresNativeSubscriptionGate();
  const [iapBusy, setIapBusy] = useState(false);

  const handleWebSubscribeClick = () => {
    if (!subscribeCheckoutUrl) {
      toast.message('Set REACT_APP_SUBSCRIBE_CHECKOUT_URL for optional web checkout.');
      return;
    }
    window.open(subscribeCheckoutUrl, '_blank', 'noopener,noreferrer');
  };

  const handleManageSubscriptions = async () => {
    if (!nativeIapConfigured) {
      toast.message('In-app products are not configured (set monthly and annual product IDs).');
      return;
    }
    setIapBusy(true);
    try {
      await openNativeSubscriptionManagement();
    } catch (e) {
      toast.error(String(e?.message || e || 'Could not open subscription settings'));
    } finally {
      setIapBusy(false);
    }
  };

  const handleRestorePurchasesSettings = async () => {
    if (!nativeIapConfigured) {
      toast.message('In-app products are not configured.');
      return;
    }
    setIapBusy(true);
    try {
      await restoreNativePurchases();
      const ok = await checkActiveProSubscription(currentUser?.uid);
      if (ok) toast.success('Purchases restored. Your subscription is active.');
      else toast.message('No active subscription found for this store account.');
    } catch (e) {
      toast.error(String(e?.message || e || 'Restore failed'));
    } finally {
      setIapBusy(false);
    }
  };

  const handleNativeSubscribeClick = async () => {
    if (!nativeIapConfigured) {
      toast.message('In-app products are not configured.');
      return;
    }
    if (!currentUser?.uid) {
      toast.message('Sign in to subscribe.');
      return;
    }
    setIapBusy(true);
    try {
      await purchaseNativeSubscription('monthly', currentUser.uid);
      const ok = await checkActiveProSubscription(currentUser.uid);
      if (ok) {
        setCachedNativeSubActiveForUid(currentUser.uid, true);
        toast.success('Subscription active.');
      }
      else toast.message('Purchase completed. Tap Restore purchases if activation is delayed.');
    } catch (e) {
      const msg = String(e?.message || e || 'Purchase failed');
      if (/cancel/i.test(msg)) toast.message('Purchase cancelled');
      else toast.error(msg);
    } finally {
      setIapBusy(false);
    }
  };

  const handleNativeSubscribeAnnual = async () => {
    if (!nativeIapConfigured) {
      toast.message('In-app products are not configured.');
      return;
    }
    if (!currentUser?.uid) {
      toast.message('Sign in to subscribe.');
      return;
    }
    setIapBusy(true);
    try {
      await purchaseNativeSubscription('annual', currentUser.uid);
      const ok = await checkActiveProSubscription(currentUser.uid);
      if (ok) {
        setCachedNativeSubActiveForUid(currentUser.uid, true);
        toast.success('Subscription active.');
      } else toast.message('Purchase completed. Tap Restore purchases if activation is delayed.');
    } catch (e) {
      const msg = String(e?.message || e || 'Purchase failed');
      if (/cancel/i.test(msg)) toast.message('Purchase cancelled');
      else toast.error(msg);
    } finally {
      setIapBusy(false);
    }
  };

  useEffect(() => {
    if (!requiresNativeSubscriptionGate()) {
      setStoreSubMeta({ plan: null, expiresAt: null });
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const d = await getNativeStoreSubscriptionDetails();
        if (!cancelled) setStoreSubMeta(d);
      } catch {
        if (!cancelled) setStoreSubMeta({ plan: null, expiresAt: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, iapBusy]);

  const customLightPreview = useMemo(
    () => buildCustomThemeFromColorInput(customColorInput || '#B565D8', '', 'light'),
    [customColorInput],
  );
  const customDarkPreview = useMemo(
    () => buildCustomThemeFromColorInput(customColorInput || '#B565D8', '', 'dark'),
    [customColorInput],
  );

  const openCustomThemeDialog = (dark) => {
    setCustomDialogDark(dark);
    const hex = getCustomThemePalette()?.baseHex || customColorInput || '#B565D8';
    setCustomColorInput(hex);
    const built = buildCustomThemeFromColorInput(hex, '', dark ? 'dark' : 'light');
    if (!built) {
      toast.error('Could not use that color');
      return;
    }
    saveCustomThemePalette(built);
    setCustomVariantDark(dark);
    changeTheme('custom');
    setCustomThemeDialogOpen(true);
  };

  const formatRenewalWords = (d) => {
    if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return '';
    }
  };

  const uid = currentUser?.uid || '';
  void aiUsageTick;
  void accountEntitleTick;
  const fsPro = uid ? readCachedFirestorePro(uid) : { active: false, expiresAtMs: null, tier: '' };
  const usageTier = uid ? getAiUsageTierForUid(uid) : 'freemium';
  const showAccountTokenMeter =
    Boolean(uid) && !AI_DAILY_TOKEN_BUDGET_DISABLED && shouldShowSettingsAiTokenMeterForUid(uid);
  const firestoreProOnly =
    Boolean(uid) &&
    usageTier === 'subscriber' &&
    isFirestoreProEntitlementActive(uid) &&
    !isNativeProSubCachedActive(uid);
  const nativeStoreSubscriber =
    Boolean(uid) && usageTier === 'subscriber' && isNativeProSubCachedActive(uid);
  const renewalPhrase = formatRenewalWords(storeSubMeta.expiresAt);
  const fsExpiryPhrase =
    fsPro.expiresAtMs != null && Number.isFinite(fsPro.expiresAtMs)
      ? formatRenewalWords(new Date(fsPro.expiresAtMs))
      : '';

  let accountStatusLine = '';
  if (!uid) {
    accountStatusLine = '';
  } else if (usageTier === 'trial' || usageTier === 'freemium') {
    accountStatusLine = 'Account status: Free Trial — please subscribe to enable full use.';
  } else if (usageTier === 'unlimited_pass') {
    accountStatusLine = 'Account status: Unlimited access.';
  } else if (firestoreProOnly) {
    accountStatusLine = fsExpiryPhrase
      ? `Account status: Pro — full access for your account until ${fsExpiryPhrase}.`
      : 'Account status: Pro — full access enabled for your account.';
  } else if (Capacitor.isNativePlatform() && nativeStoreSubscriber) {
    if (storeSubMeta.plan === 'monthly') {
      accountStatusLine = renewalPhrase
        ? `Account status: Monthly — your subscription runs out on ${renewalPhrase}.`
        : 'Account status: Monthly — your subscription is active.';
    } else if (storeSubMeta.plan === 'annual') {
      accountStatusLine = renewalPhrase
        ? `Account status: Annual — your subscription runs out on ${renewalPhrase}.`
        : 'Account status: Annual — your subscription is active.';
    } else {
      accountStatusLine = renewalPhrase
        ? `Account status: Subscribed — your subscription runs out on ${renewalPhrase}.`
        : 'Account status: Subscribed — your subscription is active.';
    }
  }

  const showWebSubscribe = Boolean(subscribeCheckoutUrl);
  const needsSubscribeUi = usageTier === 'trial' || usageTier === 'freemium';
  const showWebPayPalSubscribe =
    Boolean(currentUser?.uid) && needsSubscribeUi && isPayPalWebShell();

  // RETURN STATEMENT - ALL JSX GOES HERE
  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto w-full min-w-0 px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
        >
          <h2 className="font-body text-primary text-center mb-2" style={{ fontWeight: 600 }}>Account</h2>
          {showAccountTokenMeter ? (
            <p className="font-body text-sm text-primary text-center mb-3 break-words px-0.5">
              {formatAiTokenMeterLine(uid)}
            </p>
          ) : null}
          {currentUser && accountStatusLine ? (
            <p className="font-body text-sm text-secondary text-center mb-4 px-1 leading-relaxed">
              {accountStatusLine}
            </p>
          ) : null}

          {SHOW_SETTINGS_SUBSCRIPTION_UI && needsSubscribeUi && nativeIapConfigured ? (
            <div className="w-full max-w-sm mx-auto space-y-3 flex flex-col items-center mb-4">
              <NativeSubscriptionOptions
                firebaseUid={currentUser?.uid}
                onActivated={() => {
                  if (currentUser?.uid) setCachedNativeSubActiveForUid(currentUser.uid, true);
                }}
                onBusyChange={setIapBusy}
                buttonClassName="w-full rounded-[1.5rem] border border-theme-secondary bg-app px-4 py-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 disabled:opacity-50"
              />
              <SubscriptionLegalCopy centered />
            </div>
          ) : null}

          {SHOW_SETTINGS_SUBSCRIPTION_UI && showWebPayPalSubscribe && showWebSubscribe ? (
            <div className="w-full max-w-sm mx-auto space-y-2 flex flex-col items-center mb-4">
              <button
                type="button"
                onClick={handleWebSubscribeClick}
                className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium whitespace-nowrap transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary mx-auto"
                data-testid="settings-subscribe-btn"
              >
                <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
                Subscribe
                <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
              </button>
            </div>
          ) : null}

          {SHOW_SETTINGS_SUBSCRIPTION_UI && showWebPayPalSubscribe ? <WebSubscriptionGate /> : null}

          {SHOW_SETTINGS_SUBSCRIPTION_UI && nativeStoreSubscriber && nativeIapConfigured ? (
            <div className="w-full max-w-sm mx-auto space-y-2 flex flex-col items-center mb-4">
              {storeSubMeta.plan === 'monthly' ? (
                <button
                  type="button"
                  disabled={iapBusy}
                  onClick={() => void handleNativeSubscribeAnnual()}
                  className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium whitespace-nowrap transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary disabled:opacity-50"
                  data-testid="settings-upgrade-annual-btn"
                >
                  {iapBusy ? 'Please wait…' : 'Upgrade to annual'}
                </button>
              ) : null}
              <button
                type="button"
                disabled={iapBusy}
                onClick={() => void handleManageSubscriptions()}
                className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium whitespace-nowrap transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary disabled:opacity-50"
                data-testid="settings-manage-subscription-btn"
              >
                Manage subscription
              </button>
              <button
                type="button"
                disabled={iapBusy}
                onClick={() => void handleRestorePurchasesSettings()}
                className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium whitespace-nowrap transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary disabled:opacity-50"
                data-testid="settings-restore-purchases-subscriber-btn"
              >
                {iapBusy ? 'Please wait…' : 'Restore purchases'}
              </button>
            </div>
          ) : null}

          {SHOW_SETTINGS_SUBSCRIPTION_UI && Capacitor.isNativePlatform() && !nativeIapConfigured ? (
            <p className="font-body text-xs text-secondary text-center leading-relaxed px-1 mb-4">
              Add{' '}
              <code className="text-[11px]">REACT_APP_IAP_MONTHLY_PRODUCT_ID</code> and{' '}
              <code className="text-[11px]">REACT_APP_IAP_ANNUAL_PRODUCT_ID</code> to your build env, then rebuild the
              app to enable the subscription gate.
            </p>
          ) : null}

          <p className="font-body text-sm text-secondary text-center mb-4 break-words px-0.5">
            {currentUser?.email ? `Signed in as ${currentUser.email}` : 'Not signed in'}
          </p>

          <div className="grid grid-cols-1 gap-2 justify-items-center">
            <button
              onClick={handleLinkGoogle}
              disabled={!currentUser || hasGoogleLinked || Boolean(isLinkingProvider)}
              className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium whitespace-nowrap transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary disabled:opacity-50"
              data-testid="settings-link-google-btn"
            >
              <GoogleLogo className="h-4 w-4" />
              {hasGoogleLinked ? 'Google linked' : isLinkingProvider === 'google' ? 'Linking Google...' : 'Link Google account'}
            </button>
            {canShowAppleAuth && (
              <button
                onClick={handleLinkApple}
                disabled={!currentUser || hasAppleLinked || Boolean(isLinkingProvider)}
                className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium whitespace-nowrap transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary disabled:opacity-50"
                data-testid="settings-link-apple-btn"
              >
                <AppleLogo className="h-4 w-4" />
                {hasAppleLinked ? 'Apple linked' : isLinkingProvider === 'apple' ? 'Linking Apple...' : 'Link Apple account'}
              </button>
            )}
            <button
              onClick={() => signOutFirebase().catch((error) => console.error('Sign out failed:', error))}
              className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium whitespace-nowrap transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary"
              data-testid="settings-sign-out-btn"
            >
              Sign out
            </button>
          </div>
        </motion.div>

        {/* User Name */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
        >
          <h2 className="font-body text-primary text-center mb-4" style={{ fontWeight: 600 }}>Name:</h2>
          {editingName ? (
            <div className="flex gap-2">
              <Input value={tempName} onChange={(e) => setTempName(e.target.value)} className="flex-1 bg-theme-light border-theme-secondary rounded-xl" placeholder="Enter your name" data-testid="name-input" autoFocus />
              <Button onClick={handleNameSave} className="bg-theme-primary text-primary hover:bg-theme-hover rounded-xl" data-testid="save-name-btn">Save</Button>
            </div>
          ) : (
            <button onClick={() => { setTempName(settings.name); setEditingName(true); }} className="relative w-full p-4 rounded-xl bg-theme-light text-center flex items-center justify-center hover:bg-theme-secondary transition-colors" data-testid="edit-name-btn">
              <span className="font-body text-primary text-center">{settings.name || 'Not set'}</span>
              <ChevronRight size={18} className="absolute right-4" style={{ color: 'var(--theme-accent, #A58CD6)' }} />
            </button>
          )}
        </motion.div>

        {/* Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.17 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
        >
          <div className="space-y-4">
            <div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'small', label: 'Small Text' },
                  { key: 'xlarge', label: 'Large Text' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => handleTextScaleChange(key)}
                    className={`rounded-xl px-3 py-2 text-sm font-body transition-colors border ${((textScale === 'xlarge' ? 'xlarge' : 'small') === key) ? 'bg-theme-secondary border-theme-primary text-primary' : 'bg-theme-light border-theme-secondary text-secondary'}`}
                    data-testid={`text-size-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-2 min-w-0 space-y-2 !text-left">
              {THEME_PICKER_ROWS.map((row, rowIdx) => (
                <div key={`theme-row-${rowIdx}`} className="grid grid-cols-2 gap-2 min-w-0">
                  {row.map((cell) => {
                    if (cell.kind === 'preset') {
                      const themeData = themes[cell.key];
                      if (!themeData) return null;
                      const active = theme === cell.key;
                      return (
                        <button
                          key={cell.key}
                          type="button"
                          onClick={() => changeTheme(cell.key)}
                          className={themeSwatchClass(active)}
                          style={{ backgroundColor: themeData.light }}
                          data-testid={`theme-${cell.key}`}
                        >
                          <div className="w-full h-6 rounded-md mb-1.5" style={{ backgroundColor: themeData.primary }} />
                          <span
                            className="font-body text-[11px] theme-swatch-label block leading-tight"
                            style={{ '--swatch-label-color': getContrastTextColor(themeData.light) }}
                          >
                            {themeData.name}
                          </span>
                          {active && (
                            <div
                              className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: themeData.accent }}
                            >
                              <Check size={12} className="text-white" />
                            </div>
                          )}
                        </button>
                      );
                    }
                    const dark = cell.dark;
                    const preview = dark ? customDarkPreview : customLightPreview;
                    const active = theme === 'custom' && customVariantDark === dark;
                    const label = dark ? 'Custom Dark Theme' : 'Custom Theme';
                    return (
                      <button
                        key={dark ? 'custom-dark' : 'custom-light'}
                        type="button"
                        onClick={() => openCustomThemeDialog(dark)}
                        disabled={!preview}
                        className={`${themeSwatchClass(active)} disabled:opacity-50`}
                        style={{ backgroundColor: preview?.light || (dark ? '#0E0E12' : '#F4F1FA') }}
                        data-testid={dark ? 'custom-theme-dark-option' : 'custom-theme-light-option'}
                      >
                        <div
                          className="w-full h-6 rounded-md mb-1.5"
                          style={{ backgroundColor: preview?.primary || (dark ? '#7c3aed' : '#c084fc') }}
                        />
                        <span
                          className="font-body text-[11px] theme-swatch-label block leading-tight"
                          style={{
                            '--swatch-label-color': getContrastTextColor(preview?.light || (dark ? '#0E0E12' : '#F4F1FA')),
                          }}
                        >
                          {label}
                        </span>
                        {active && (
                          <div
                            className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: preview?.accent || '#8E6ECC' }}
                          >
                            <Check size={12} className="text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            <Dialog open={customThemeDialogOpen} onOpenChange={setCustomThemeDialogOpen}>
              <DialogContent className="max-w-md bg-theme-light border-theme-secondary">
                <DialogHeader>
                  <DialogTitle className="font-body text-primary text-left">
                    {customDialogDark ? 'Custom Dark Theme' : 'Custom Theme'}
                  </DialogTitle>
                  <DialogDescription className="sr-only">Pick a base color for this custom palette.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 pt-1">
                  <input
                    type="color"
                    aria-label="Custom theme base color"
                    className="h-14 w-full cursor-pointer rounded-xl border-2 border-theme-secondary bg-theme-light"
                    value={customColorInput}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCustomColorInput(v);
                      const built = buildCustomThemeFromColorInput(v, '', customDialogDark ? 'dark' : 'light');
                      if (built) {
                        saveCustomThemePalette(built);
                        setCustomVariantDark(customDialogDark);
                        changeTheme('custom');
                      } else {
                        toast.error('Could not use that color');
                      }
                    }}
                    data-testid="custom-theme-color-dialog-input"
                  />
                  <Button
                    type="button"
                    className="w-full bg-theme-primary text-primary hover:bg-theme-hover rounded-xl"
                    onClick={() => setCustomThemeDialogOpen(false)}
                  >
                    Done
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="pt-2 space-y-3">
              <div className="flex items-center justify-between py-1">
                <p className="font-body text-sm text-primary">Set Passcode</p>
                <Switch checked={securityType === 'passcode'} onCheckedChange={handlePasscodeToggle} />
              </div>

              {biometricsAvailable && (
                <div className="flex items-center justify-between py-1">
                  <p className="font-body text-sm text-primary">Face ID / Touch ID</p>
                  <Switch checked={securityType === 'biometric'} onCheckedChange={handleBiometricsToggle} />
                </div>
              )}

              <div className="pt-3 mt-2 space-y-3">
                <div className="flex items-center justify-between py-1">
                  <p className="font-body text-sm text-primary">Voice Input</p>
                  <Switch
                    checked={Boolean(voicePrefs.voiceInputEnabled)}
                    onCheckedChange={(v) => updateVoicePrefs({ voiceInputEnabled: v })}
                    data-testid="voice-input-enabled"
                  />
                </div>
                {voicePrefs.voiceInputEnabled && (
                  <div className="space-y-2 !text-left">
                    <label className="block font-body text-xs text-secondary !text-left" htmlFor="voice-stt-lang">Recognition language</label>
                    <select
                      id="voice-stt-lang"
                      value={voicePrefs.sttLang || 'en-US'}
                      onChange={(e) => updateVoicePrefs({ sttLang: e.target.value })}
                      className="w-full rounded-xl border border-theme-secondary bg-theme-light px-3 py-2 font-body text-sm text-primary"
                    >
                      <option value="en-US">English (US)</option>
                      <option value="en-GB">English (UK)</option>
                      <option value="en-AU">English (Australia)</option>
                      <option value="en-IN">English (India)</option>
                    </select>
                  </div>
                )}

                <div className="flex items-center justify-between py-1">
                  <p className="font-body text-sm text-primary">Text to Speak</p>
                  <Switch
                    checked={Boolean(voicePrefs.speakEnabled)}
                    onCheckedChange={(v) => updateVoicePrefs({ speakEnabled: v })}
                    data-testid="speak-enabled"
                  />
                </div>
                {voicePrefs.speakEnabled && (
                  <div className="space-y-3 !text-left">
                    <div className="space-y-2">
                      <span className="block font-body text-xs text-secondary !text-left">Read-aloud speed</span>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { rate: 0.9, label: 'Slower' },
                          { rate: 1, label: 'Normal' },
                          { rate: 1.1, label: 'Faster' },
                          { rate: 1.2, label: 'Fast' },
                        ].map(({ rate, label }) => (
                          <button
                            key={rate}
                            type="button"
                            onClick={() => updateVoicePrefs({ ttsRate: rate })}
                            className={`rounded-xl px-3 py-2 text-sm font-body transition-colors border ${Number(voicePrefs.ttsRate) === rate ? 'bg-theme-secondary border-theme-primary text-primary' : 'bg-theme-light border-theme-secondary text-secondary'}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="block font-body text-xs text-secondary !text-left" htmlFor="voice-tts-lang">Voice language / region</label>
                      <select
                        id="voice-tts-lang"
                        value={voicePrefs.ttsLang || 'en-US'}
                        onChange={(e) => updateVoicePrefs({ ttsLang: e.target.value, ttsVoiceURI: '' })}
                        className="w-full rounded-xl border border-theme-secondary bg-theme-light px-3 py-2 font-body text-sm text-primary"
                      >
                        <option value="en-US">English (US)</option>
                        <option value="en-GB">English (UK)</option>
                        <option value="en-AU">English (Australia)</option>
                        <option value="en-IN">English (India)</option>
                        <option value="en-IE">English (Ireland)</option>
                        <option value="en-ZA">English (South Africa)</option>
                      </select>
                    </div>
                    {ttsVoices.length > 0 && (
                      <div className="space-y-2">
                        <label className="block font-body text-xs text-secondary !text-left" htmlFor="voice-tts-voice">Speaking voice (accent)</label>
                        <select
                          id="voice-tts-voice"
                          value={voicePrefs.ttsVoiceURI || ''}
                          onChange={(e) => updateVoicePrefs({ ttsVoiceURI: e.target.value })}
                          className="w-full max-w-full min-w-0 rounded-xl border border-theme-secondary bg-theme-light px-3 py-2 font-body text-sm text-primary"
                        >
                          <option value="">System default for language</option>
                          {ttsVoices
                            .filter((v) => {
                              const pref = (voicePrefs.ttsLang || 'en-US').toLowerCase();
                              const lang = String(v.lang || '').toLowerCase();
                              return lang === pref || lang.startsWith(`${pref.split('-')[0]}-`);
                            })
                            .slice(0, 48)
                            .map((v) => (
                              <option key={v.voiceURI} value={v.voiceURI}>
                                {v.name || v.voiceURI}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="pt-2 space-y-3">
              <div className="flex items-center justify-between py-1">
                <p className="font-body text-sm text-primary">Daily Reminder</p>
                <Switch checked={notifSettings.enabled} onCheckedChange={handleNotificationToggle} disabled={!notifSupported} data-testid="notification-toggle" />
              </div>
              {notifSettings.enabled && (
                <div className="flex justify-end">
                  <input type="time" value={notifSettings.time} onChange={handleTimeChange} className="time-input-theme bg-theme-light border border-theme-secondary rounded-xl px-3 py-2 font-body text-primary" data-testid="notification-time" />
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Help */}
        <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          onClick={() => navigate('/help')}
          className="w-full bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4 transition-all hover:shadow-interactive"
          data-testid="help-btn"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-body text-primary flex-1 text-center">Help & Instructions</h2>
            <ChevronRight size={18} style={{ color: 'var(--theme-accent, #A58CD6)' }} />
          </div>
        </motion.button>

        <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }}
          onClick={() => navigate('/export')}
          className="w-full bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4 transition-all hover:shadow-interactive"
          data-testid="export-btn"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-body text-primary flex-1 text-center">Import & Export</h2>
            <ChevronRight size={18} style={{ color: 'var(--theme-accent, #A58CD6)' }} />
          </div>
        </motion.button>

        {/* Privacy */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
        >
          <div className="space-y-3">
            <h2 className="font-body text-primary">Your Data Is Yours</h2>
            <p className="font-body text-sm text-secondary leading-relaxed">
              In RE-BUILD-ME-PRO, your goals, journal entries, and coaching chat are stored in the cloud for your signed-in account.
            </p>
            <p className="font-body text-sm text-secondary leading-relaxed">
              Deleting the app from your phone does not delete your cloud data.
            </p>
            <p className="font-body text-sm text-secondary leading-relaxed">
              Use Delete Account below if you want to permanently erase your cloud data and account access.
            </p>
            <div className="pt-2">
              <button
                onClick={() => setShowDeleteAccountDialog(true)}
                disabled={!currentUser || isDeletingAccount}
                className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium whitespace-nowrap transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary disabled:opacity-50"
                data-testid="delete-cloud-account-btn"
              >
                {isDeletingAccount ? 'Deleting Account...' : 'Delete Account'}
              </button>
            </div>
            <div className="pt-2">
              <div className="flex flex-wrap items-center gap-4">
                <a href="https://www.re-build-me.com/p/privacy-policy-for-re-build-me-pro.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-body text-sm text-theme-accent hover:text-theme-dark transition-colors" data-testid="privacy-policy-link">
                Privacy Policy <ExternalLink size={14} />
                </a>
                <a href={TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-body text-sm text-theme-accent hover:text-theme-dark transition-colors" data-testid="terms-of-use-link">
                  Terms of Use <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Blog Link - Styled Box */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.01 }}
          className="bg-theme-light rounded-3xl p-6 border border-theme-secondary mb-4"
          data-testid="blog-section"
        >
          <div>
            <p className="font-body text-sm text-secondary leading-relaxed mb-4">
              Learn more about the rebuilding cycle:
              <br /><br />
              Action creates evidence.
              <br />
              Evidence builds belief.
              <br />
              Belief becomes identity.
              <br /><br />
              Created by someone who is also rebuilding himself.
              <br /><br />
              All the best,<br />
              Justin Tee
            </p>

            {/* Blog Link - Matching Home screen button style */}
            <a
              href="https://www.re-build-me.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary mb-4"
              data-testid="blog-link"
            >
              Read My Story
            </a>

            {/* Contact section */}
            <div className="pt-3">
              <p className="font-body text-sm text-secondary leading-relaxed mb-3">
                Have feedback or just want to say hello?
              </p>

              {/* Email Link - Matching Home screen button style */}
              <a
                href="mailto:mail@re-build-me.com"
                className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary"
                data-testid="email-link"
              >
                Email Me
                <Mail size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </motion.div>

        {/* Version */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-center py-4">
          <p className="font-body text-xs text-secondary/60 tabular-nums">RE-BUILD-ME-PRO {APP_VERSION_LABEL}</p>
        </motion.div>
      </div>

      {/* Passcode Dialog */}
      <Dialog open={showPasscodeDialog} onOpenChange={setShowPasscodeDialog}>
        <DialogContent className="bg-theme-light rounded-3xl max-w-xs mx-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-center">{passcodeStep === 'enter' ? 'Set Passcode' : 'Confirm Passcode'}</DialogTitle>
            <DialogDescription className="text-center font-body text-secondary">
              {passcodeStep === 'enter' ? 'Enter a 4-digit passcode' : 'Re-enter your passcode to confirm'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-6 py-4">
  <div className="flex justify-center gap-3">
    {[0, 1, 2, 3].map((i) => (
      <input
        key={i}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={1}
        value={(passcodeStep === 'enter' ? passcodeValue : confirmPasscode)[i] || ''}
        onChange={(e) => {
          const newValue = e.target.value;
          if (newValue && !/^\d$/.test(newValue)) return;
          
          const currentValue = passcodeStep === 'enter' ? passcodeValue : confirmPasscode;
          const newCode = currentValue.split('');
          newCode[i] = newValue || '';
          const newCodeString = newCode.join('');
          
          if (passcodeStep === 'enter') {
            setPasscodeValue(newCodeString);
            // Auto-focus next input
            if (newValue && i < 3) {
              document.getElementById(`passcode-input-${i + 1}`)?.focus();
            }
          } else {
            setConfirmPasscode(newCodeString);
            if (newValue && i < 3) {
              document.getElementById(`confirm-passcode-input-${i + 1}`)?.focus();
            }
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Backspace') {
            const currentValue = passcodeStep === 'enter' ? passcodeValue : confirmPasscode;
            if (!currentValue[i] && i > 0) {
              document.getElementById(passcodeStep === 'enter' ? `passcode-input-${i - 1}` : `confirm-passcode-input-${i - 1}`)?.focus();
            }
          }
        }}
        id={passcodeStep === 'enter' ? `passcode-input-${i}` : `confirm-passcode-input-${i}`}
        className="w-14 h-14 text-center text-xl font-bold rounded-xl border-2 border-theme-primary bg-theme-light focus:border-theme-accent focus:outline-none"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        data-testid={`passcode-input-${i}`}
      />
    ))}
  </div>
  
  <Button 
    onClick={handlePasscodeSubmit} 
    disabled={(passcodeStep === 'enter' ? passcodeValue : confirmPasscode).length !== 4} 
    className="w-full bg-theme-primary text-primary hover:bg-theme-hover rounded-full" 
    data-testid="passcode-submit"
  >
    {passcodeStep === 'enter' ? 'Continue' : 'Set Passcode'}
  </Button>
</div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteAccountDialog} onOpenChange={setShowDeleteAccountDialog}>
        <DialogContent className="max-w-xs w-[min(100%,20rem)] gap-3 border-theme-secondary bg-theme-light p-5 rounded-3xl shadow-ambient sm:max-w-sm mx-auto">
          <DialogHeader className="space-y-2">
            <DialogTitle className="font-heading text-lg text-primary flex items-center justify-center gap-2 leading-snug">
              <AlertTriangle size={18} className="shrink-0" />
              Delete Account
            </DialogTitle>
            <DialogDescription className="font-body text-secondary text-sm leading-relaxed text-center">
              This permanently deletes your signed-in RE-BUILD-ME-PRO cloud data and account access.
              You will be asked to confirm identity with Apple/Google sign-in to complete deletion.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 pt-1">
            <button
              type="button"
              onClick={handleDeleteCloudAccount}
              disabled={isDeletingAccount}
              className="inline-flex w-56 max-w-full mx-auto items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary disabled:opacity-60 disabled:hover:translate-y-0"
              data-testid="confirm-delete-cloud-account-btn"
            >
              {isDeletingAccount ? 'Deleting...' : 'Yes, delete account'}
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteAccountDialog(false)}
              className="inline-flex w-56 max-w-full mx-auto items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium transition-all border border-theme-secondary bg-theme-light text-primary hover:bg-theme-hover disabled:opacity-60"
              data-testid="cancel-delete-cloud-account-btn"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </PageWrapper>
  );
};

export default Settings;